import React from 'react';
import { Formik } from 'formik';
import { Alert, Col, DatePicker, Divider, Row, Tooltip, Typography } from 'antd';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment/moment';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { countries } from '../../../../const/countries';
import { languages } from '../../../../const/languages';
import { dateFieldFormat, targetPlatformOptions } from '../../../../const/system';
import { mkValidationSchema } from './FormCreateEditPushNotificationSchedule.const';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import getPushNotificationOptions from '../../../../lib/getPushNotificationOptions';
import canInteractWithSchedule from '../../../../lib/canInteractWithSchedule';
import disableDateBeforeToday from '../../../../lib/disableDateBeforeToday';
import CountrySelector from '../../../../components/CountrySelector';
import Spinner from '../../../../components/Spinner';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { useGetPushNotificationsQuery } from '../../api/pushNotificationsApiSlice';



const { Title } = Typography;

const FormCreateEditPushNotificationSchedule = ({
  initialErrors = {},
  initialValues = {},
  onSubmit = () => {},
  isSubmitting = false,
  isEdit = false,
}) => {
  const intl = useIntl();

  const {
    data: { data: pushNotifications = [] } = {},
    isFetching: isFetchingPushNotifications,
  } = useGetPushNotificationsQuery({ queryParams: 'limit=0' });


  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={mkValidationSchema}
      onSubmit={onSubmit}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched, resetForm }) => {
        const hasUnpublishedChanges = !moment(values?.published_at).isSame(moment(values?.updated_at));

        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-push-notification' })}
                        name='push_notification_id'
                        required
                      >
                        <Select
                          loading={isFetchingPushNotifications}
                          allowClear
                          showSearch
                          name="push_notification_id"
                          value={isFetchingPushNotifications ? undefined : values.push_notification_id}
                          placeholder={intl.formatMessage({ id: 'ui-general-push-notification' })}
                          options={getPushNotificationOptions(pushNotifications)}
                          onChange={async (value) => {
                            await setFieldValue('push_notification_id', value);
                          }}
                          filterOption={(input, option) => makeSelectFilterOption(option.title, input)}
                        />
                      </FormItem>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={
                          <div>
                            {intl.formatMessage({ id: 'ui-general-scheduled-at' })}
                            {!canInteractWithSchedule(values, false) && values?.scheduled_at && (
                              <Tooltip className="hp-ml-8" title={intl.formatMessage({ id: 'push-notifications-schedule-time-outdated' })}>
                                <ExclamationCircleOutlined className="hp-text-color-warning-1" />
                              </Tooltip>
                            )}
                          </div>
                        }
                        name='scheduled_at'
                        required
                      >
                        <DatePicker
                          showToday
                          name='scheduled_at'
                          showTime
                          format={dateFieldFormat}
                          disabledDate={disableDateBeforeToday}
                          value={values?.scheduled_at ? moment(values.scheduled_at).utc(false) : null}
                          onChange={async (date, dateString) => {
                            await setFieldValue('scheduled_at', !isEmpty(dateString) ? moment(dateString).utc(true) : null);
                          }}
                          className={!canInteractWithSchedule(values, false) && values?.scheduled_at ? 'hp-input-warning' : ''}
                        />
                      </FormItem>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        name='use_utc_zero_time'
                        label={
                          <Row className='w-full' justify="space-between" align="middle">
                            <IntlMessages id="ui-general-use-utc" />
                            <Tooltip
                              placement="top"
                              title={
                                <>
                                  <IntlMessages id="ui-general-use-utc-info" />
                                  <div>
                                    <Link to='https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/734461960/Use+UTC+0' target="_blank">
                                      <u><IntlMessages id="ui-general-documentation-link" /></u>
                                    </Link>
                                  </div>
                                </>
                              }
                            >
                              <InfoCircleOutlined className='hp-ml-8' />
                            </Tooltip>
                          </Row>
                        }
                      >
                        <Switch name="use_utc_zero_time" />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
                <Col md={24} xs={24}>
                  <Divider />
                  <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-targets" /></Title>
                  <Row gutter={16}>
                    <Col span={12}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-min-version' })}
                        name='min_version'
                      >
                        <Input
                          name="min_version"
                          placeholder={intl.formatMessage({ id: 'ui-general-targets-min-version-placeholder' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={12}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-max-version' })}
                        name='max_version'
                      >
                        <Input
                          name="max_version"
                          placeholder={intl.formatMessage({ id: 'ui-general-targets-max-version-placeholder' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-platform' })}
                        name='platform'
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          name="platform"
                          options={targetPlatformOptions}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-platform' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-countries' })}
                        name='countries'
                      >
                        <CountrySelector
                          onSelectAll={async () => {
                            await setFieldValue('countries', countries.map((country) => country.value).sort());
                          }}
                          isDisabledSelectAll={values?.target?.countries?.length === countries.length}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          name='countries'
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-countries' })}
                        />
                      </FormItem>
                    </Col>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-languages' })}
                        name='languages'
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          name="languages"
                          options={languages}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-languages' })}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {(!isEdit || dirty || hasUnpublishedChanges) && (
                <>
                  <Alert
                    message={<IntlMessages id="ui-general-edit-has-changes" />}
                    type="info"
                    showIcon
                  />

                  <Row justify='end' className='hp-mt-24 hp-mb-24'>
                    <Col>
                      <Row>
                        <IntlMessages id="ui-general-publish-changes" />
                        <Switch name="is_published" className='hp-ml-16' />
                      </Row>
                    </Col>
                  </Row>
                </>
              )}

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty || !canInteractWithSchedule(values, false)}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreateEditPushNotificationSchedule.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool,
};


export default FormCreateEditPushNotificationSchedule;
